import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { skipToken } from '@reduxjs/toolkit/query';
import { UID_NAME_TUPLES } from 'constants/dataSets';
import { VALUE_CHAIN_SECTION } from 'constants/formFields';
import {
  useGetNaceCodeTreeQuery, useUidNameTuplesQuery,
} from 'store/slices/dataSets/apis/dataSetsApi';
import { selectUidNameTuples } from 'store/slices/dataSets/selectors';
import { useGetLocationsCountriesQuery } from 'store/slices/geoLocations/apis/geoLocationsApi';
import { useGetCustomerDataQuery, useUidNameTuplesVCQuery } from 'store/slices/valueChainMap/apis/valueChainMapApi';
import { selectUidNameTuplesVC } from 'store/slices/valueChainMap/selectors';
import getCustomerDefaultValues from 'utils/formHelpers/getCustomerDefaultValues';
import { createCustomerSchema } from 'utils/validators';

import type { TCreateCustomerSchema } from 'utils/validators';
import type ICreateCustomerForm from '../interfaces/ICreateCustomerForm';

const useCreateCustomerForm = ({
  recordId, isPageLoading, valueChainMapId, valueChainSectionCurrent,
}: ICreateCustomerForm) => {
  const { data } = useGetCustomerDataQuery(
    recordId ? { chainId: `${recordId}` } : skipToken,
    { refetchOnMountOrArgChange: true },
  );
  const hasExistingUniqueIdentifier = Boolean(data?.data.uniqueIdentifier?.trim());
  const isUniqueIdentifierEditable = recordId ? Boolean(data?.data) && !hasExistingUniqueIdentifier : true;
  const { data: profitCenterIds, isLoading: isProfitCenterIdsLoading } = useUidNameTuplesVCQuery({
    types: [UID_NAME_TUPLES.profitCenter],
    valueChainMapId,
  }, {
    selectFromResult: (result) => ({ ...result, data: selectUidNameTuplesVC(result.data) }),
  });

  const { data: naceCodeTreeData } = useGetNaceCodeTreeQuery();

  const { data: uidNameTuples } = useUidNameTuplesQuery({
    types: [UID_NAME_TUPLES.profitCenter],
  }, {
    selectFromResult: (result) => ({ ...result, data: selectUidNameTuples(result.data) }),
  });

  const { data: locationsCountries } = useGetLocationsCountriesQuery();

  const {
    control,
    handleSubmit,
    formState,
    formState: { isDirty, isLoading },
    reset,
    setError,
    getValues,
    watch,
    setValue,
  } = useForm<TCreateCustomerSchema>({
    mode: 'onSubmit',
    resolver: yupResolver(createCustomerSchema),
    defaultValues: getCustomerDefaultValues({
      values: data?.data,
      naceCodeTreeData,
      uidNameTuples,
      locationsCountries: locationsCountries?.data || [],
      valueChainSectionCurrent,
    }),
  });

  useEffect(() => {
    if (data?.data && !isPageLoading && !isProfitCenterIdsLoading) {
      reset(getCustomerDefaultValues(
        {
          values: data?.data,
          naceCodeTreeData,
          uidNameTuples,
          profitCenterIds,
          locationsCountries: locationsCountries?.data || [],
          valueChainSectionCurrent,
        },
      ));
    }
  }, [reset, data, isPageLoading, naceCodeTreeData, uidNameTuples,
    profitCenterIds, isProfitCenterIdsLoading, locationsCountries, valueChainSectionCurrent]);

  useEffect(() => {
    if (valueChainSectionCurrent && !isPageLoading && !isProfitCenterIdsLoading) {
      setValue(VALUE_CHAIN_SECTION, {
        value: valueChainSectionCurrent,
        label: valueChainSectionCurrent,
      });
    }
  }, [valueChainSectionCurrent, setValue, isPageLoading, isProfitCenterIdsLoading]);

  const fieldsName = Object.keys(getValues());

  return {
    control,
    handleSubmit,
    isDirty,
    reset,
    isLoading,
    setError,
    fieldsName,
    watch,
    setValue,
    profitCenterIds,
    formState,
    isUniqueIdentifierEditable,
  };
};

export default useCreateCustomerForm;
